/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import './style/mobileNavigation.scss';

import classNames from 'classnames';
import React, {
    ForwardedRef,
    forwardRef,
    MouseEventHandler,
    ReactElement, useEffect,
    useImperativeHandle,
    useMemo, useRef,
    useState
} from 'react';
import {useMatch, useNavigate} from 'react-router';
import {NavigationItem} from "../util/interfaces";
import {AwesomeIcon, hasHover} from "../../../index";

interface Props {
    onClickItem?: (item: NavigationItem) => MouseEventHandler;
    navigationItems: Array<NavigationItem>;
    className?: string;
    menuIcon?: ReactElement<any>;
    id?: string;
    keepOpen?: true;
}

export interface MobileNavigationRef {
    toggle: (forceState?: boolean) => void;
}

function MobileNavigation(props: Props, ref: ForwardedRef<MobileNavigationRef>) {
    const {navigationItems, onClickItem, className, menuIcon, id, keepOpen} = props;
    const [open, setOpen] = useState(false);
    const [topItems, bottomItems] = useMemo(() => {
        const topItems: Array<NavigationItem> = [];
        const bottomItems: Array<NavigationItem> = [];
        for (const item of navigationItems) {
            if (item.placement === 'bottom') bottomItems.push(item);
            // Default placement top
            else topItems.push(item);
        }
        return [topItems, bottomItems];
    }, [navigationItems]);
    const navigate = useNavigate();
    const menuRef = useRef<HTMLDivElement>(null);

    function toggle(forceState?: boolean) {
        if (forceState !== undefined) setOpen(forceState);
        else setOpen(!open);
    }

    function onClickIcon() {
        toggle();
    }

    function _onClick(item: NavigationItem) {
        return function (evt: React.MouseEvent) {
            if (!item.passPropagation) evt.stopPropagation();
            if (!keepOpen) toggle(false);
            if (onClickItem) return onClickItem(item)(evt);
            else if (item.onClick) item.onClick(evt);
            else if (item.route) _navigate(item.route);
            else throw new Error(`Mobile navigation item ${item.id} has no click handler.`)
        }
    }

    function _navigate(route: string) {
        if (route.startsWith('http'))
            window.open(route, '_blank');
        else navigate(route);
    }

    function renderNavigationItem(item: NavigationItem) {
        return (
            <MobileNavigationItem key={item.id} item={item} onClick={_onClick}/>
        );
    }

    function renderIcon() {
        let icon: React.ReactElement;
        if (!!menuIcon) {
            const className = classNames(menuIcon.props.className, 'mobile-navigation-icon');
            icon = React.cloneElement(menuIcon, {...menuIcon.props, className});
        } else icon = <AwesomeIcon name='bars' className='mobile-navigation-icon'/>;
        return (
            <div className='mobile-navigation-icon-container' onClick={onClickIcon}>
                {icon}
            </div>
        )
    }

    function renderMenu() {
        return (
            <>
                {open && <div className='click-catcher'/>}
                <div className='mobile-navigation-container' ref={menuRef}>
                    <AwesomeIcon name='times' className='mobile-navigation-close' onClick={onClickIcon}/>
                    <div className='mobile-navigation-items'>
                        {topItems.map(renderNavigationItem)}
                    </div>
                    <div className='mobile-navigation-items'>
                        {bottomItems.map(renderNavigationItem)}
                    </div>
                </div>
            </>
        )
    }

    function outsideClickListener() {
        const menu = menuRef.current;
        if (!hasHover(menu)) toggle(false);
    }

    useEffect(() => {
        window.setTimeout(() => {
            if (open) document.addEventListener('click', outsideClickListener);
            else document.removeEventListener('click', outsideClickListener);
        });
        return function () {
            document.removeEventListener('click', outsideClickListener);
        }
    }, [open]);

    useImperativeHandle(ref, () => ({toggle}));

    return (
        <div className={classNames('dvrd-mobile-navigation', open && 'open', className)} id={id}>
            {renderIcon()}
            {renderMenu()}
        </div>
    )
}

interface ItemProps {
    item: NavigationItem;
    onClick: (item: NavigationItem) => MouseEventHandler;
}

function MobileNavigationItem(props: ItemProps) {
    const {item, onClick} = props;
    const isActive = item.route && Boolean(useMatch(item.route));
    return (
        <div key={item.id} className={classNames('mobile-navigation-item', isActive && 'active', item.className)}
             id={item.id} onClick={onClick(item)}>
            {item.content}
        </div>
    )
}

export default forwardRef<MobileNavigationRef, Props>(MobileNavigation);